import React, { useState, useEffect, Suspense } from 'react';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import BottomNav from './components/BottomNav';
import SubscriptionModal from './components/SubscriptionModal';
import ContentProtection from './components/ContentProtection';
import './App.css';

// Lazy Load Pages
const HomeFeed = React.lazy(() => import('./components/HomeFeed'));
const UserGrid = React.lazy(() => import('./components/UserGrid'));
const UserProfile = React.lazy(() => import('./components/UserProfile'));
const SignUp = React.lazy(() => import('./components/SignUp'));
const LiveStream = React.lazy(() => import('./components/LiveStream'));
const ProfileDashboard = React.lazy(() => import('./components/ProfileDashboard'));

// Story Components
import StoryUploadModal from './components/StoryUploadModal';
import StoryViewModal from './components/StoryViewModal';

import { createUserProfile, updateUserProfile, updateUserStatus } from './services/UserManager';
import { startStream, endStream, listenToActiveStreams } from './services/StreamService';
import { GenderSelectModal, ProfileUpdatePrompt, PhoneNumberModal } from './components/OnboardingModals';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';

function App() {
  const [activeTab, setActiveTab] = useState('Home');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [cameraMode, setCameraMode] = useState('user'); // 'user' (front) or 'environment' (back)

  // User Management State
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showPhotoPrompt, setShowPhotoPrompt] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);

  // Stories State
  const [showStoryUpload, setShowStoryUpload] = useState(false);
  const [viewingStoryUser, setViewingStoryUser] = useState(null);

  // Global Streams Sync (Real-Sync 27.0)
  const [activeStreamsMap, setActiveStreamsMap] = useState(new Map()); // uid -> streamData

  // Load user data from localStorage on mount and set Online Status
  useEffect(() => {
    const storedData = localStorage.getItem('yatsite_userData');
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setCurrentUserData(parsed);
      setIsSignedUp(true);

      // Set Online Status
      // Set Online Status
      if (parsed.uid) {
        // Infer city from timezone
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const city = timeZone ? timeZone.split('/')[1]?.replace(/_/g, ' ') || 'Unknown' : 'Unknown';
        const dataWithCity = { ...parsed, city };
        updateUserStatus(parsed.uid, true, null, dataWithCity);
      }
    }
  }, []);

  // Heartbeat to keep user "Online"
  useEffect(() => {
    if (!currentUserData?.uid) return;

    const interval = setInterval(() => {
      // Infer city from timezone
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const city = timeZone ? timeZone.split('/')[1]?.replace(/_/g, ' ') || 'Unknown' : 'Unknown';
      const dataWithCity = { ...currentUserData, city };
      updateUserStatus(currentUserData.uid, true, null, dataWithCity);
    }, 5 * 60 * 1000); // Every 5 minutes

    const handleBeforeUnload = () => {
      // Best effort to set offline
      // Note: Navigator.sendBeacon might be better but updateUserStatus uses Firestore SDK
      // We can't guarantee this runs
      updateUserStatus(currentUserData.uid, false);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUserData]);

  // Check for Missing Gender or Photo Prompt
  useEffect(() => {
    if (!isSignedUp || !currentUserData) return;

    // 1. Gender check
    if (!currentUserData.gender) {
      setShowGenderModal(true);
    } else {
      setShowGenderModal(false);
    }

    // 2. Photo Prompt check (6 hours)
    if (currentUserData.joinedAt) {
      const joinTime = new Date(currentUserData.joinedAt).getTime();
      const sixHours = 6 * 60 * 60 * 1000;
      const now = Date.now();

      const hasPhotos = (currentUserData.images && currentUserData.images.length > 0) || currentUserData.photoURL;

      if (!hasPhotos && (now - joinTime > sixHours)) {
        setShowPhotoPrompt(true);
      } else {
        setShowPhotoPrompt(false);
      }


    }

    // 3. Phone Number Prompt check (2 minutes after gender selection)
    const checkPhonePrompt = () => {
      // Don't show if already has number
      if (currentUserData.whatsapp || currentUserData.mpesa) {
        setShowPhoneNumberModal(false);
        return;
      }

      const genderSetTime = localStorage.getItem('yatsite_genderSetAt');
      // If we don't have a timestamp, maybe they set gender long ago or we didn't track it.
      // Strategy: If gender is set but no timestamp, assume we start timer NOW or handle it gracefully.
      // Let's rely on the timestamp being set during handleGenderSelect.
      // If user already had gender set before this update, they might miss this prompt unless we check "joinedAt" too?
      // User said "after the gender pop up is filled", implying this flow.

      if (genderSetTime) {
        const timeSet = parseInt(genderSetTime, 10);
        const twoMinutes = 2 * 60 * 1000;
        const now = Date.now();

        if (now - timeSet >= twoMinutes) {
          setShowPhoneNumberModal(true);
        } else {
          // Not yet time
          setShowPhoneNumberModal(false);
        }
      }
    };
    checkPhonePrompt();
  }, [isSignedUp, currentUserData]);

  // Global Stream Listener (Real-Sync 27.0)
  useEffect(() => {
    const unsubscribe = listenToActiveStreams((streams) => {
      const streamMap = new Map();
      streams.forEach(s => streamMap.set(s.id, s));
      setActiveStreamsMap(streamMap);
    });
    return () => unsubscribe();
  }, []);

  // Real-time User Data Listener (Important for Payments/Status)
  useEffect(() => {
    if (!currentUserData?.uid) return;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUserData.uid), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const remoteData = docSnapshot.data();
        setCurrentUserData(prev => {
          // If prev is null (shouldn't be since we have uid), use remote
          if (!prev) return remoteData;

          const newData = { ...prev, ...remoteData };

          // Only update if changes detected (basic check)
          if (JSON.stringify(newData) !== JSON.stringify(prev)) {
            localStorage.setItem('yatsite_userData', JSON.stringify(newData));
            return newData;
          }
          return prev;
        });
      }
    });

    return () => unsubscribe();
  }, [currentUserData?.uid]);

  const handleGenderSelect = async (gender) => {
    if (!currentUserData) return;

    const updatedUser = { ...currentUserData, gender };

    // Default 'interestedIn' based on gender if not set? 
    // Logic: Male -> Female, Female -> Male is the common default assumption but better to let user pick.
    // For now just save gender.

    setCurrentUserData(updatedUser);
    localStorage.setItem('yatsite_userData', JSON.stringify(updatedUser));

    // Set timestamp for phone prompt
    localStorage.setItem('yatsite_genderSetAt', Date.now().toString());

    try {
      await updateUserProfile(updatedUser.uid || updatedUser.firebaseUid, { gender });
    } catch (e) {
      console.error("Failed to update gender", e);
    }

    setShowGenderModal(false);
  };

  const handlePhoneNumberSave = async (number) => {
    if (!currentUserData) return;

    const updatedUser = {
      ...currentUserData,
      whatsapp: number,
      mpesa: number
    };

    setCurrentUserData(updatedUser);
    localStorage.setItem('yatsite_userData', JSON.stringify(updatedUser)); // Persist locally
    setShowPhoneNumberModal(false);

    try {
      await updateUserProfile(updatedUser.uid || updatedUser.firebaseUid, {
        whatsapp: number,
        mpesa: number
      });
      // Optional: Notify success
    } catch (e) {
      console.error("Failed to update phone number", e);
    }
  };

  const handleUpdateProfileNow = () => {
    setShowPhotoPrompt(false);
    setIsEditingProfile(true);
    // If not already on 'Me' tab, switch to it
    setActiveTab('Me');
  };

  const handleSignUpSuccess = async (data) => {
    localStorage.setItem('yatsite_userData', JSON.stringify(data));
    setCurrentUserData(data);
    setIsSignedUp(true);
    // Be sure to set active tab to 'Me' or stay there to show profile
    setActiveTab('Home');

    // Create/Update Firestore Profile
    try {
      await createUserProfile(data);
      await updateUserStatus(data.uid, true); // Set Online
    } catch (error) {
      console.error("Error syncing profile to Firestore:", error);
    }
  };

  const handleLogout = () => {
    if (currentUserData?.uid) {
      updateUserStatus(currentUserData.uid, false);
    }
    localStorage.removeItem('yatsite_userData');
    setIsSignedUp(false);
    setCurrentUserData(null);
    setActiveTab('Home');
  };



  const handleDeleteAccount = () => {
    localStorage.removeItem('yatsite_userData');
    setIsSignedUp(false);
    setCurrentUserData(null);
    setActiveTab('Home');
  };

  // Auto-light grading (Night Mode) effect
  useEffect(() => {
    const checkTime = () => {
      const hour = new Date().getHours();
      // Activate night mode between 7 PM (19:00) and 7 AM (07:00)
      if (hour >= 19 || hour < 7) {
        document.body.classList.add('night-mode');
      } else {
        document.body.classList.remove('night-mode');
      }
    };

    checkTime(); // Check immediately on load
    const interval = setInterval(checkTime, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Mock current user for the "Go Live" feature
  const currentUser = {
    id: 'me',
    name: 'James',
    images: ['https://api.dicebear.com/7.x/avataaars/svg?seed=James'],
    interests: ['Streaming', 'Tech'],
    location: 'Nairobi',
    // Pass the stream if it exists. 
    // Note: We need to ensure logic handles this correctly when passed as 'selectedUser'
    videoStream: localStream
  };

  const handleUserClick = (user) => {
    console.log("App: User clicked:", user.name, "ID:", user.id, "UID:", user.uid, "isStreaming:", user.isStreaming);
    const streamMeta = activeStreamsMap.get(user.uid || user.id);
    console.log("App: Stream metadata found:", !!streamMeta);
    setSelectedUser(user);
  };

  const handleUnlockClick = () => {
    setShowSubscriptionModal(true);
  };

  const handlePostStory = () => {
    if (!isSignedUp) {
      setActiveTab('Me');
      alert("Please Sign In/Sign Up to post a story! ✨");
      return;
    }
    setShowStoryUpload(true);
  };

  const handleSubscribe = () => {
    setIsSubscribed(true);
    setShowSubscriptionModal(false);
  };





  const handleGoLive = async () => {
    try {
      // 1. Cleanup existing stream if any
      if (localStream) {
        localStream.getTracks().forEach(track => track.stop());
      }

      let stream;
      try {
        // Request Natural High-Quality Feed
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: cameraMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: true
        });
      } catch (err) {
        console.warn("High-res camera failed, trying basic config...", err);
        // Fallback 1: Portrait but very permissive
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: cameraMode, width: 640, height: 480 },
            audio: true
          });
        } catch (innerErr) {
          console.warn("Permissive config failed, trying LAST RESORT...", innerErr);
          // Fallback 2: The most basic request possible
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true
          });
        }
      }

      setLocalStream(stream);

      // 2. Set Selected User to Self (Optimistic UI Update)
      // Do this BEFORE backend call to prevent hanging if network/firestore is slow
      setSelectedUser({
        ...currentUserData,
        videoStream: stream,
        isOwnStream: true
      });

      // 3. Start Backend Stream (Non-blocking or background)
      if (currentUserData) {
        startStream(currentUserData).catch(e => {
          console.error("Failed to start stream backend", e);
        });
      }

    } catch (error) {
      console.error("Error accessing camera:", error);
      alert(`Camera Access Failed:\n${error.name}: ${error.message}\n\nEnsure you are on HTTPS and have allowed permissions.`);
    }
  };

  const handleBackToFeed = async () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
      // Backend cleanup
      if (currentUserData?.uid) {
        try {
          await endStream(currentUserData.uid);
        } catch (e) { console.error("Error ending stream", e); }
      }
    }
    setSelectedUser(null);
  };

  const handleUpdateWallet = (coins) => {
    if (currentUserData) {
      setCurrentUserData(prev => ({ ...prev, coins }));
      // The Firestore listener will catch the actual DB update, 
      // but this makes the UI snappy.
    }
  };

  const handleSwitchCamera = async () => {
    const newMode = cameraMode === 'user' ? 'environment' : 'user';
    setCameraMode(newMode);

    if (localStream) {
      // 1. Stop current tracks
      localStream.getTracks().forEach(track => track.stop());

      // 2. Request new stream with new facingMode
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: newMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 }
          },
          audio: true
        });

        // 3. Update states
        setLocalStream(stream);

        // Update selectedUser if it's the own stream view
        if (selectedUser?.isOwnStream) {
          setSelectedUser(prev => ({
            ...prev,
            videoStream: stream
          }));
        }
      } catch (error) {
        console.error("Error switching camera:", error);
        alert("Failed to switch camera. This device may not support the requested mode.");
      }
    }
  };

  // Render content based on state
  const renderContent = () => {
    // 1. Priority: Own Stream (Moved to switch case 'LiveStream')
    // Old block removed to prevent conflicts


    // 2. Viewing other user (Real-Sync 27.0: Metadata enrichment)
    if (selectedUser) {
      // Find latest metadata for this user if they are streaming
      const streamMeta = activeStreamsMap.get(selectedUser.uid || selectedUser.id);
      const isActuallyStreaming = !!streamMeta || selectedUser.isStreaming;

      if (selectedUser.videoStream || isActuallyStreaming) {
        return (
          <ErrorBoundary>
            <LiveStream
              activeStream={selectedUser.videoStream || streamMeta || null}
              user={{ ...selectedUser, isStreaming: isActuallyStreaming }}
              currentUser={currentUserData}
              onClose={selectedUser.isOwnStream ? handleBackToFeed : () => setSelectedUser(null)}
              isOwnStream={selectedUser.isOwnStream || false}
              onSwitchCamera={handleSwitchCamera}
              onUpdateWallet={handleUpdateWallet}
            />
          </ErrorBoundary>
        );
      }

      // NO VIDEO ATTACHED -> Show Static Profile
      // Enriched with isActuallyStreaming to show "Watch Live" button if applicable
      return (
        <ErrorBoundary>
          <UserProfile
            user={{ ...selectedUser, isStreaming: isActuallyStreaming }}
            onBack={() => setSelectedUser(null)}
            onWatchClick={(user) => handleUserClick(user)}
            isSubscribed={isSubscribed}
            onUnlockClick={handleUnlockClick}
          />
        </ErrorBoundary>
      );
    }

    switch (activeTab) {
      case 'Home':
        return (
          <HomeFeed
            onUserClick={handleUserClick}
            onUnlockClick={handleUnlockClick}
            currentUser={currentUserData}
            onPostStory={handlePostStory}
            onStoryClick={(userStories) => setViewingStoryUser(userStories)}
          />
        );
      case 'Explore':
        return <UserGrid title="Explore" onUserClick={handleUserClick} onUnlockClick={handleUnlockClick} filterGender={currentUserData?.interestedIn} />;
      case 'Online':
        return <UserGrid title="Active Now" showOnlineStatus={true} onUserClick={handleUserClick} onUnlockClick={handleUnlockClick} />; // Show ALL online users

      case 'Me':
        if (!isSignedUp) {
          return (
            <SignUp
              onSignUpSuccess={handleSignUpSuccess}
              isSignedUp={false}
            />
          );
        }

        if (isEditingProfile) {
          return (
            <SignUp
              onSignUpSuccess={() => setIsEditingProfile(false)} // Return to dashboard on save
              isSignedUp={true}
              onLogout={handleLogout}
              onDeleteAccount={handleDeleteAccount}
              userData={currentUserData}
            />
          );
        }

        return (
          <ProfileDashboard
            user={currentUserData}
            onLogout={handleLogout}
            onEditProfile={() => setIsEditingProfile(true)}
          />
        );
      default:
        return <HomeFeed onUserClick={handleUserClick} onUnlockClick={handleUnlockClick} />;
    }
  };

  return (
    <div className="app-container">
      {/* <ContentProtection /> */}
      {!selectedUser && (
        <Header
          onLogoClick={() => setActiveTab('Home')}
          user={currentUserData}
          isSignedUp={isSignedUp}
          onLogout={handleLogout}
          onNavigate={setActiveTab}
          onPostStory={handlePostStory}
        />
      )}

      <main className="main-content" style={{ paddingBottom: !selectedUser ? 80 : 0 }}>
        <Suspense fallback={
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: '#000',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            Loading Application...
          </div>
        }>
          {renderContent()}
        </Suspense>
      </main>

      {!selectedUser && (
        <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      )}

      {showGenderModal && (
        <GenderSelectModal onSelect={handleGenderSelect} />
      )}

      {showPhotoPrompt && (
        <ProfileUpdatePrompt onUpdateNow={handleUpdateProfileNow} />
      )}

      {showPhoneNumberModal && (
        <PhoneNumberModal onSave={handlePhoneNumberSave} />
      )}

      {showSubscriptionModal && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          onSubscribe={handleSubscribe}
          user={currentUserData}
        />
      )}

      {showStoryUpload && currentUserData && (
        <StoryUploadModal
          user={currentUserData}
          onClose={() => setShowStoryUpload(false)}
          onUploadSuccess={() => {
            setShowStoryUpload(false);
            // Optionally refresh or show message
          }}
        />
      )}

      {viewingStoryUser && (
        <StoryViewModal
          userStories={viewingStoryUser}
          onClose={() => setViewingStoryUser(null)}
        />
      )}

      {!selectedUser && activeTab === 'Home' && (
        <button className="suspended-live-btn" onClick={handleGoLive}>
          Go Live
        </button>
      )}
    </div>
  );
}

export default App;
